import React from "react";
import { Sprout, RefreshCw } from "lucide-react";

/**
 * Top-level React error boundary that catches anything thrown during
 * render / lifecycle / hooks across the app and shows a calm, on-brand
 * fallback instead of CRA's red overlay.
 *
 * What this does:
 *  - Catches render-time + lifecycle errors only (not async event-handler
 *    errors — those need their own try/catch which we already have via
 *    `formatApiError`).
 *  - Logs the error + component-stack to the console so devs can still
 *    debug from DevTools.
 *  - Exposes a "Try again" button that resets the boundary and a "Reload"
 *    button that hard-refreshes (escape hatch when the in-place reset
 *    doesn't pick up new code).
 *
 * What this deliberately does NOT do:
 *  - Send errors to a remote logger. We don't have one wired up and
 *    rolling our own would just bloat the bundle.
 *  - Auto-retry. Auto-retry hides recurring bugs.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, errorInfo) {
        // Surface the failure in DevTools so it isn't silently swallowed.
        // eslint-disable-next-line no-console
        console.error("[rooteed] uncaught render error:", error, errorInfo);
        this.setState({ errorInfo });
    }

    reset = () => {
        this.setState({ error: null, errorInfo: null });
    };

    reload = () => {
        // Hard reload — useful when the broken state lives in cached
        // module memory and a soft reset can't dislodge it.
        window.location.reload();
    };

    render() {
        if (!this.state.error) return this.props.children;

        const message = this.state.error?.message || String(this.state.error);

        return (
            <div
                className="min-h-screen flex items-center justify-center px-6 py-20 paper-texture"
                data-testid="error-boundary"
                role="alert"
            >
                <div className="max-w-lg w-full journal-card p-10 text-center">
                    <div className="w-14 h-14 rounded-full bg-[var(--vm-accent)]/15 flex items-center justify-center mx-auto mb-5">
                        <Sprout className="w-6 h-6 text-[var(--vm-accent)]" strokeWidth={1.5} />
                    </div>
                    <h1 className="font-serif-display text-3xl text-[var(--vm-text)]">
                        A little leaf curled up
                    </h1>
                    <p className="text-sm text-[var(--vm-text-secondary)] mt-3 leading-relaxed">
                        Something on this page didn't render the way it should. Your data is safe —
                        it's a display hiccup, not a lost harvest.
                    </p>
                    <pre
                        className="mt-5 p-3 rounded-md bg-[var(--vm-input)]/40 border border-[var(--vm-border)] text-xs text-[var(--vm-text-muted)] text-left overflow-auto max-h-32"
                        data-testid="error-boundary-message"
                    >
                        {message}
                    </pre>
                    <div className="flex flex-wrap justify-center gap-3 mt-6">
                        <button
                            type="button"
                            onClick={this.reset}
                            data-testid="error-boundary-retry"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] px-5 py-2.5 text-sm font-medium inline-flex items-center"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" /> Try again
                        </button>
                        <button
                            type="button"
                            onClick={this.reload}
                            data-testid="error-boundary-reload"
                            className="rounded-full border border-[var(--vm-border)] hover:bg-[var(--vm-input)] text-[var(--vm-text)] px-5 py-2.5 text-sm"
                        >
                            Reload page
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
